import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { OrderService } from "@/services/order-services";
import { SupplierService } from "@/services/supplier-service";
import {
  Document,
  PDFDownloadLink,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, Download } from "lucide-react";

export const Route = createFileRoute("/_main/orders/generate-invoice/$orderId")(
  {
    component: InvoiceGenerator,
    loader: async ({ params }: { params: { orderId: string } }) => {
      const order = await OrderService.getOrderById(params.orderId);

      if (!order?.supplier) {
        throw new Error("Order has no supplier");
      }

      const supplier = await SupplierService.getSupplier(order.supplier);

      return { order, supplier };
    },
  },
);

interface InvoiceItem {
  name: string;
  quantity: number;
  uom: string | null;
  amount?: number;
}

// Define proper interface for InvoicePDF props
interface InvoicePDFProps {
  invoiceData: {
    invoiceNumber: string;
    orderNumber: string;
    company: {
      name: string;
      address: string;
      // email: string;
      // phone: string;
    };
    billTo: {
      name: string;
      company: string;
      email: string;
      phone: string;
      address: string;
    };
    dates: {
      invoice: string;
      due: string;
    };
    items: InvoiceItem[];
  };
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
}

// Define styles for PDF outside component to prevent re-creation
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
  },
  section: {
    margin: 10,
    padding: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 12,
    color: "#4b5563",
    marginBottom: 5,
  },
  companyInfo: {
    fontSize: 10,
    color: "#4b5563",
    textAlign: "right",
  },
  separator: {
    height: 1,
    backgroundColor: "#e5e7eb",
    width: "100%",
    marginVertical: 10,
  },
  billInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 20,
  },
  billTo: {
    fontSize: 10,
    width: "50%",
  },
  dates: {
    fontSize: 10,
    width: "50%",
    textAlign: "right",
  },
  table: {
    width: "100%",
    marginVertical: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    padding: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomColor: "#e5e7eb",
    borderBottomWidth: 1,
    padding: 8,
    fontSize: 9,
  },
  col1: {
    width: "40%",
  },
  col2: {
    width: "30%",
    textAlign: "right",
  },
  col3: {
    width: "30%",
    textAlign: "right",
  },
  unit: {
    fontSize: 8,
    color: "#4b5563",
  },
  summary: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  summaryDetails: {
    width: "30%",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    fontSize: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    borderTopColor: "#e5e7eb",
    borderTopWidth: 1,
    paddingTop: 5,
    fontSize: 12,
    fontWeight: "bold",
  },
});

// Create PDF Document component outside main component
const InvoicePDF = ({
  invoiceData,
  subtotal,
  tax,
  shipping,
  total,
}: InvoicePDFProps) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>INVOICE</Text>
          <Text style={styles.subtitle}>#{invoiceData.invoiceNumber}</Text>
          <Text style={styles.subtitle}>Order #{invoiceData.orderNumber}</Text>
        </View>
        <View>
          <Text style={styles.companyInfo}>{invoiceData.company.name}</Text>
          <Text style={styles.companyInfo}>{invoiceData.company.address}</Text>
          {/* <Text style={styles.companyInfo}>{invoiceData.company.email}</Text>
          <Text style={styles.companyInfo}>{invoiceData.company.phone}</Text> */}
        </View>
      </View>

      <View style={styles.separator} />

      <View style={styles.billInfo}>
        <View style={styles.billTo}>
          <Text style={{ fontSize: 10, color: "#4b5563", marginBottom: 5 }}>
            Bill To:
          </Text>
          <Text style={{ fontSize: 10, fontWeight: "bold" }}>
            {invoiceData.billTo.name}
          </Text>
          <Text style={{ fontSize: 10, fontWeight: "bold" }}>
            {invoiceData.billTo.company}
          </Text>
          <Text style={{ fontSize: 9, color: "#4b5563" }}>
            {invoiceData.billTo.address}
          </Text>
        </View>
        <View style={styles.dates}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginBottom: 5,
            }}
          >
            <Text style={{ fontSize: 10, color: "#4b5563" }}>
              Invoice Date:{" "}
            </Text>
            <Text style={{ fontSize: 10, fontWeight: "bold" }}>
              {invoiceData.dates.invoice}
            </Text>
          </View>
          <View style={{ flexDirection: "row", justifyContent: "flex-end" }}>
            <Text style={{ fontSize: 10, color: "#4b5563" }}>Due Date: </Text>
            <Text style={{ fontSize: 10, fontWeight: "bold" }}>
              {invoiceData.dates.due}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.col1]}>Item</Text>
          <Text style={[styles.col2]}>Quantity</Text>
          <Text style={[styles.col3]}>Unit</Text>
        </View>

        {invoiceData.items.map((item: InvoiceItem, index: number) => (
          <View key={index} style={styles.tableRow}>
            <View style={styles.col1}>
              <Text>{item.name}</Text>
            </View>
            <Text style={styles.col2}>{item.quantity}</Text>
            <Text style={styles.col3}>{item.uom}</Text>
          </View>
        ))}
      </View>

      <View style={styles.summary}>
        <View style={styles.summaryDetails}>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Subtotal</Text>
            <Text>${subtotal.toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Tax (0%)</Text>
            <Text>${tax.toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Shipping:</Text>
            <Text>${shipping.toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Total</Text>
            <Text>${total.toFixed(2)}</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

function InvoiceGenerator() {
  const { order, supplier } = Route.useLoaderData();

  // Extract order details
  const orderItems = Array.isArray(order.order_details)
    ? order.order_details
    : [];

  const invoiceData = {
    invoiceNumber: `INV-${order.id}-${new Date().getFullYear()}`,
    orderNumber: order.id.toString(),
    company: {
      name: supplier?.name ?? "",
      address: supplier?.address ?? "",
    },
    billTo: {
      name: order.restaurant_name,
      company: order.restaurant_name,
      email: "", // Not available in order data
      phone: "", // Not available in order data
      address: order.restaurant_address,
    },
    dates: {
      invoice: new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      due: order.expected_delivery_date
        ? new Date(order.expected_delivery_date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : new Date(
            new Date().setDate(new Date().getDate() + 30),
          ).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }),
    },
    items: orderItems,
  };

  // Since we don't have pricing information in the order data, we'll use placeholder values
  const subtotal = 0;
  const tax = 0;
  const shipping = 0;
  const total = subtotal + tax + shipping;

  return (
    <div className="min-h-screen p-8">
      <div className="mb-8 flex items-center justify-between">
        <Button variant="ghost" asChild>
          <a href={`/orders/${order.id}`}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Order
          </a>
        </Button>
        <PDFDownloadLink
          document={
            <InvoicePDF
              invoiceData={invoiceData}
              subtotal={subtotal}
              tax={tax}
              shipping={shipping}
              total={total}
            />
          }
          fileName={`invoice-${order.id}.pdf`}
        >
          {({ loading, error }) => (
            <Button disabled={loading}>
              <Download className="mr-2 h-4 w-4" />
              {loading ? "Preparing..." : "Download PDF"}
            </Button>
          )}
        </PDFDownloadLink>
      </div>

      <div className="mx-auto flex max-w-4xl flex-col gap-8 rounded-lg bg-white shadow-lg">
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between">
            <div>
              <h1 className="font-medium text-xl">INVOICE</h1>
              <p className="text-gray-600">#{invoiceData.invoiceNumber}</p>
              <p className="text-gray-600 text-xs">
                Order #{invoiceData.orderNumber}
              </p>
            </div>
            <div className="text-right text-sm">
              <h2 className="font-medium">{invoiceData.company.name}</h2>
              <p className="text-gray-600">{invoiceData.company.address}</p>
              {/* <p className="text-gray-600">{invoiceData.company.email}</p>
              <p className="text-gray-600">{invoiceData.company.phone}</p> */}
            </div>
          </div>

          <Separator className="my-4 w-full" />

          {/* Billing Info */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="mb-1 text-gray-600 text-sm">Bill To:</h3>
              <p className="font-medium text-sm">{invoiceData.billTo.name}</p>
              <p className="font-medium text-sm">
                {invoiceData.billTo.company}
              </p>
              <p className="text-gray-600 text-sm">
                {invoiceData.billTo.address}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-right text-sm">
              <div className="flex flex-col gap-2">
                <span className="text-gray-600">Invoice Date:</span>
                <span className="ml-4 font-medium">
                  {invoiceData.dates.invoice}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-gray-600">Due Date:</span>
                <span className="ml-4 font-medium">
                  {invoiceData.dates.due}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <table className="w-full px-8">
          <thead>
            <tr className="text-left">
              <th className="bg-gray-100 p-4 font-medium text-xs">Item</th>
              <th className="bg-gray-100 p-4 text-right font-medium text-xs">
                Quantity
              </th>
              <th className="bg-gray-100 p-4 text-right font-medium text-xs">
                Unit
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {invoiceData.items.map((item, index) => (
              <tr
                key={`${item.name}-${index}`}
                className="border-gray-100 border-b"
              >
                <td className="p-4">{item.name}</td>
                <td className="p-4 text-right">{item.quantity}</td>
                <td className="p-4 text-right">{item.uom}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary */}
        <div className="mt-2 flex flex-col items-end p-6">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tax (0%)</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Shipping:</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between border-gray-200 border-t pt-2">
              <span className="font-medium">Total</span>
              <span className="font-medium text-lg">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
